/* ============================================================
   Taglio — storefront page behaviour
   Progressive enhancement for the routes outside the Build page.

   Everything here is optional: with JavaScript off the product form still
   posts the first variant, the collection still lists and paginates, and
   the search form still submits. Nothing is rendered BY this file, so a
   failed load degrades to a plain, working page rather than a blank one.

   WHY THERE IS NO COLLECTION / SORT / SEARCH CODE HERE
   Those are server-side concerns in Shopify: sort order and pagination are
   query parameters that the Liquid reads (`collection.sort_by`,
   `paginate`), so doing them in JS would mean two sources of truth for the
   same state and a page that disagrees with its own URL. The toolbar is a
   plain GET form for that reason.
   ============================================================ */
(function () {
  'use strict';

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  // ---------------------------------------------------------------
  // Money
  // ---------------------------------------------------------------
  // Shopify's money_format is a merchant-editable string like "${{amount}}".
  // We format with it rather than hardcoding a "$", so a merchant who changes
  // the store currency or format does not get a page that renders two
  // different currencies side by side (Liquid on the server, JS on the client).
  function formatMoney(cents, format) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    var fmt = format || '${{amount}}';
    var amount = (n / 100).toFixed(2);
    var noDec = String(Math.round(n / 100));

    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
  }

  // ---------------------------------------------------------------
  // Product variant selector
  // ---------------------------------------------------------------
  // The server renders a plain radio group per option, so the form is valid
  // on its own. This layer watches those radios, resolves the selected
  // COMBINATION to a real variant, and keeps Price, Compare-at, the buy
  // button and the URL in step.
  //
  // It deliberately does not "pick" a variant for the customer: a combination
  // Shopify does not sell is reported as unavailable rather than silently
  // snapped to a neighbouring variant, because silently selling a different
  // size than the one displayed is worse than saying "sold out".
  function initProduct() {
    var root = document.querySelector('[data-tg-product]');
    if (!root) return;

    var dataEl = root.querySelector('[data-tg-variants]');
    var idInput = root.querySelector('[data-tg-variant-id]');
    // A single-variant product renders no variant matrix, so there is nothing
    // to sync out of it.
    //
    // NOTE: idInput is deliberately NOT required here. Lane products render no
    // add-to-cart form (they are bought through the builder), so they have no
    // hidden variant input. Requiring one would make this whole function bail
    // silently on exactly the pages where the price and the size pills matter
    // most — the price would freeze on the first variant and the Size radios
    // would stop updating anything.
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
      if (window.console) console.warn('[taglio] bad variant JSON', e);
      return;
    }
    if (!variants || !variants.length) return;

    var moneyEl = root.querySelector('[data-tg-money]');
    var moneyFormat = '${{amount}}';
    if (moneyEl) {
      try { moneyFormat = (JSON.parse(moneyEl.textContent || '{}').format) || moneyFormat; } catch (e) { }
    }

    var priceEl = root.querySelector('[data-tg-price]');
    var addBtn = root.querySelector('[data-tg-add]');
    var addLabel = root.querySelector('[data-tg-add-label]');
    var radios = root.querySelectorAll('[data-tg-opt-index]');

    // Lane products have no form, so their primary action is a Build link
    // (sections/taglio-product.liquid, `data-tg-build-link`). The pill choice
    // has to travel with it: the server rendered the link from the FIRST
    // variant's size, so without this the customer picks Large, clicks
    // "Personalise this tag", and lands in the builder on Medium.
    var buildLink = root.querySelector('[data-tg-build-link]');

    // Option names are not in the variants JSON (it only has option1/2/3), so
    // read them off the DOM. Mapping by NAME is the point: base-bundles lists
    // Pack first and Size second, so the size lives at a different index on
    // that product than on the others.
    var sizeIndex = 0;
    each(root.querySelectorAll('[data-tg-opt]'), function (wrap) {
      var idx = parseInt(wrap.getAttribute('data-tg-opt'), 10);
      var lab = wrap.querySelector('.tg-opt__label');
      var name = lab ? (lab.textContent || '').trim().toLowerCase() : '';
      if (name === 'size') sizeIndex = idx;
    });

    // A tag size is S/M/L. Anything else means this combination carries no
    // usable size, in which case the builder's own default is the honest
    // answer — better than writing a junk value the builder then ignores.
    function sizeKeyFor(v) {
      if (!sizeIndex) return '';
      var raw = v['option' + sizeIndex];
      if (!raw) return '';
      var k = String(raw).charAt(0).toUpperCase();
      return (k === 'S' || k === 'M' || k === 'L') ? k : '';
    }

    // Sold-out combinations disable the link rather than hiding it: the pills
    // stay put so the customer can see which size is the problem and pick
    // another, instead of the page appearing to have no way forward.
    function setBuildAvailable(ok) {
      if (!buildLink) return;
      if (ok) {
        buildLink.removeAttribute('aria-disabled');
        buildLink.removeAttribute('data-tg-unavailable');
      } else {
        buildLink.setAttribute('aria-disabled', 'true');
        buildLink.setAttribute('data-tg-unavailable', '1');
      }
    }

    // A disabled link still navigates on click, so swallow it. Only needed
    // once; the guard keeps a re-render from stacking listeners.
    if (buildLink && !buildLink.getAttribute('data-tg-guarded')) {
      buildLink.setAttribute('data-tg-guarded', '1');
      buildLink.addEventListener('click', function (ev) {
        if (buildLink.getAttribute('aria-disabled') === 'true') {
          ev.preventDefault();
          return;
        }
        // Rewrite at click time as well, so the href is right even if a
        // change event was missed (e.g. the pill was set by a URL param).
        var opts = selectedOptions();
        var v = findVariant(opts);
        if (v) applySizeToBuildLink(v);
      });
    }

    function applySizeToBuildLink(v) {
      if (!buildLink) return;
      var key = sizeKeyFor(v);
      if (!key) return;
      var href = buildLink.getAttribute('href') || '';
      try {
        var u = new URL(href, window.location.origin);
        u.searchParams.set('size', key);
        buildLink.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (e) { /* relative-href edge case: leave the server's value */ }
    }

    function selectedOptions() {
      var out = [];
      each(radios, function (r) {
        if (!r.checked) return;
        var idx = parseInt(r.getAttribute('data-tg-opt-index'), 10) - 1;
        out[idx] = r.value;
      });
      return out;
    }

    function findVariant(opts) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var k = 0; k < opts.length; k++) {
          if (opts[k] === undefined) continue;
          var have = v['option' + (k + 1)];
          if (have !== opts[k]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    function render(v) {
      if (priceEl) {
        var html = formatMoney(v.price, moneyFormat);
        if (v.compare_at_price && Number(v.compare_at_price) > Number(v.price)) {
          html += '<span class="tg-prod__was">' + formatMoney(v.compare_at_price, moneyFormat) + '</span>';
        }
        priceEl.innerHTML = html;
      }

      if (idInput) idInput.value = v.id;

      // Lane products: carry the size into the Build link instead of a form.
      applySizeToBuildLink(v);
      setBuildAvailable(v.available !== false);

      if (addBtn) addBtn.disabled = !v.available;
      if (addLabel) {
        addLabel.textContent = v.available
          ? (addLabel.getAttribute('data-label-add') || addLabel.textContent)
          : (addLabel.getAttribute('data-label-sold') || 'Sold out');
      }

      // Keep the URL honest so a shared link reopens the same variant. Only
      // the query string changes, so no history entry is added per click.
      if (window.history && window.history.replaceState) {
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', v.id);
          window.history.replaceState(null, '', u.pathname + u.search + u.hash);
        } catch (e) { }
      }
    }

    function sync() {
      var v = findVariant(selectedOptions());
      if (!v) {
        // A combination that is not sold. Say so rather than lying about it.
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Sold out';
        // ...and stop the Build link too. It is the only way to buy on a lane
        // product, so leaving it live would send the customer into the builder
        // to design a size this product does not sell — they would only find
        // out after building the whole tag.
        setBuildAvailable(false);
        return;
      }
      render(v);
    }

    each(radios, function (r) { r.addEventListener('change', sync); });

    // If the URL already names a variant, reflect it in the radios so the
    // pills agree with the price the server rendered.
    var wanted = null;
    try { wanted = new URL(window.location.href).searchParams.get('variant'); } catch (e) { }
    if (wanted) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(wanted)) {
          each(radios, function (r) {
            var idx = parseInt(r.getAttribute('data-tg-opt-index'), 10);
            var have = variants[i]['option' + idx];
            if (have !== undefined) r.checked = (r.value === have);
          });
          break;
        }
      }
    }
  }

  // ---------------------------------------------------------------
  // Collection sort (progressive enhancement only)
  // ---------------------------------------------------------------
  // The toolbar is a real GET form, so it works with JS off. This just
  // submits it as soon as the customer picks, instead of making them press
  // a button they cannot see.
  function initSort() {
    var sel = document.querySelector('[data-tg-sort]');
    if (!sel) return;
    sel.addEventListener('change', function () {
      var form = sel.form;
      if (form) form.submit();
    });
  }

  function boot() {
    initProduct();
    initSort();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
